import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Loading, MockBadge } from "@/components/Loading";

const SAMPLE = { title: "Grandma's Bedtime Story", message: "Sweet dreams, my darlings. I love you to the moon.", playback: { type: "sample" } };

const useNoIndex = () =>
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

const Player = ({ p }) => {
  const cls = "aspect-[9/16] w-full bg-black";
  if (p.type === "youtube") return <iframe className={cls} src={`https://www.youtube-nocookie.com/embed/${p.video_id}?rel=0&playsinline=1`} title="Family memory" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="memory-youtube-player" />;
  if (p.type === "external") return <a className="btn-primary w-full" href={p.url} data-testid="memory-external-link">Play the video</a>;
  const src = p.type === "sample" ? "/img/sample.mp4?v=2" : `${process.env.REACT_APP_BACKEND_URL}${p.src}`;
  return <video className={`${cls} object-contain`} src={src} controls playsInline poster={p.type === "sample" ? "/img/sample-poster.jpg?v=2" : undefined} data-testid="memory-video-player" />;
};

const RemovalLink = ({ token }) => {
  const [f, setF] = useState({ name: "", email: "", reason: "" });
  const [open, setOpen] = useState(false);
  const send = async () => {
    try {
      await api.post(`/memory/${token}/removal-request`, f);
      toast.success("Thank you. We received your request.");
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="text-base text-[#5C4A3E] underline" data-testid="memory-report-button">Request removal of this video</button></DialogTrigger>
      <DialogContent className="bg-[#FFFDF9]">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Request removal</DialogTitle></DialogHeader>
        <input className="field" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="memory-removal-name-input" />
        <input className="field" type="email" placeholder="Your email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="memory-removal-email-input" />
        <textarea className="field min-h-[100px]" placeholder="Why should it be removed?" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="memory-removal-reason-input" />
        <button className="btn-primary" onClick={send} disabled={!f.email || f.reason.length < 3} data-testid="memory-removal-submit-button">Send request</button>
      </DialogContent>
    </Dialog>
  );
};

export default function Memory() {
  const { token } = useParams();
  const [m, setM] = useState(token === "sample" ? SAMPLE : null);
  const [missing, setMissing] = useState(false);
  useNoIndex();
  useEffect(() => {
    if (token === "sample") return;
    api.get(`/memory/${token}`).then(({ data }) => setM(data)).catch(() => setMissing(true));
  }, [token]);

  if (missing) return (
    <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="memory-not-found">
      <h1 className="text-4xl font-bold text-[#4A2A18]">This memory isn't available</h1>
      <p className="mt-4 text-lg text-[#4A3B32]">The link may be incorrect, or the keepsake is still being prepared.</p>
    </div>
  );
  if (!m) return <Loading />;
  return (
    <div className="mx-auto max-w-md px-4 py-10 text-center" data-testid="memory-page">
      <img src="/img/logo.jpg" alt="Voices 2 Remember" className="mx-auto h-14 w-14 rotate-[-4deg] rounded-sm border-4 border-white shadow" />
      <h1 className="mt-6 text-4xl font-bold leading-tight text-[#4A2A18]" data-testid="memory-title">{m.title}</h1>
      {m.removed ? (
        <p className="paper-card mt-8 p-8 text-lg text-[#4A3B32]" data-testid="memory-removed">This video was removed at the owner's request.</p>
      ) : (
        <div className="relative mt-8">
          <span className="tape -top-3 left-1/2 z-10 -translate-x-1/2 rotate-[-3deg]" />
          <div className="polaroid"><Player p={m.playback} /></div>
          {m.playback?.simulated && <p className="mt-3"><MockBadge testid="memory-mock-badge">Preview playback, not on YouTube yet</MockBadge></p>}
        </div>
      )}
      {m.message && <p className="font-script mt-8 text-3xl leading-snug text-[#4A3B32]" data-testid="memory-message">"{m.message}"</p>}
      <div className="mt-12 space-y-4">
        <Link to="/" className="btn-secondary w-full" data-testid="memory-make-your-own-link">Make your own keepsake</Link>
        {token !== "sample" && !m.removed && <RemovalLink token={token} />}
      </div>
    </div>
  );
}
